// CLEAN, STABLE LEDGER LIST PAGE WITH SORTING
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import LedgerSummaryStrip from "./components/LedgerSummaryStrip";
import LedgerTable from "./components/LedgerTable";
import useLedgerData from "./hooks/useLedgerData";
// @ts-ignore: allow side-effect CSS import without module declarations
import "../Ledger.css";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { useUser } from "src/shared/context/UserContext";
import { pageTitle } from "src/shared/utils/brand";
import { useUserTheme } from "src/shared/utils/useUserTheme";

// Interface for paginated navigation
interface PaginatedNavigation {
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  totalRecords: number;
}

const Ledger: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const preserved = location.state || null;
  
  // URL params
  const urlPage = parseInt(searchParams.get("pageNumber") || "1", 10) - 1;
  const urlPageSize = parseInt(searchParams.get("pageSize") || "10", 10);
  const urlSearchTerm = searchParams.get("searchBy") || "";
  const urlSortField = searchParams.get("sortField") || "";
  const urlSortOrder = searchParams.get("sortOrder") || "";
  
  // State
  const [currentPage, setCurrentPage] = useState(urlPage);
  const { userConfigration } = useUser();
  const [rowsPerPage, setRowsPerPage] = useState(userConfigration?.tableDefaultSize || urlPageSize);
  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [showZeroOutstanding, setShowZeroOutstanding] = useState(false);
  const [sortField, setSortField] = useState(urlSortField);
  const [sortOrder, setSortOrder] = useState(urlSortOrder);
  const { sidebarThemeClass } = useUserTheme();

  // Restore preserved state from navigation
  useEffect(() => {
    document.title = pageTitle("Ledger List");
    
    if (preserved?.fromPage) {
      const params = new URLSearchParams();

      params.set("pageNumber", preserved.fromPage);
      params.set("pageSize", preserved.fromPageSize);
      if (preserved.searchTerm) params.set("searchBy", preserved.searchTerm);
      if (preserved.sortField) params.set("sortField", preserved.sortField);
      if (preserved.sortOrder) params.set("sortOrder", preserved.sortOrder);

      setCurrentPage(preserved.fromPage - 1);
      setRowsPerPage(preserved.fromPageSize);
      setSearchTerm(preserved.searchTerm || "");
      setSortField(preserved.sortField || "");
      setSortOrder(preserved.sortOrder || "");

      setSearchParams(params, { replace: true });
      navigate(location.pathname, { replace: true });
    }
  }, [preserved, navigate, location.pathname, setSearchParams]);

  // Update URL params when pagination/search/sorting changes
  useEffect(() => {
    const params = new URLSearchParams();
    params.set("pageNumber", String(currentPage + 1));
    params.set("pageSize", String(rowsPerPage));

    if (searchTerm.trim() !== "") {
      params.set("searchBy", searchTerm.trim());
    }

    if (sortField) {
      params.set("sortField", sortField);
    }

    if (sortOrder) {
      params.set("sortOrder", sortOrder);
    }

    setSearchParams(params, { replace: true });
  }, [currentPage, rowsPerPage, searchTerm, sortField, sortOrder, setSearchParams]);

  // Use the ledger data hook with sorting parameters
  const { 
    ledgerRecords, 
    totalOutstanding, 
    totalRecords, 
    formatCurrency, 
    loading,
    fetchCurrentPageRecords,
  } = useLedgerData(
    currentPage, 
    rowsPerPage, 
    searchTerm, 
    showZeroOutstanding,
    sortField,
    sortOrder
  );

  // Custom row click handler
  const handleRowClick = async (id: string) => {
    // Fetch current page data
    const currentPageData = await fetchCurrentPageRecords();

    // Create pagination info for navigation
    const paginationInfo: PaginatedNavigation = {
      currentPage: currentPage + 1,
      pageSize: rowsPerPage,
      searchTerm,
      totalRecords,
    };

    // Navigate to ledger detail page
    navigate(`/v1/ledger/${id}`, {
      state: {
        customers: currentPageData,
        paginationInfo,
        searchTerm,
        fromPage: currentPage + 1,
        fromPageSize: rowsPerPage,
        showZeroOutstanding,
        sortField,
        sortOrder,
      },
    });
  };

  // Handle sorting change from table
  const handleSortChange = (sortData: { field: string; orderby: string }) => {
    setSortField(sortData.field);
    setSortOrder(sortData.orderby);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  return (
    <div className={`flex h-full flex-col flex-1 p-1.5`}>
      <div className={`ledger-wrapper shadow-inner rounded-lg ${sidebarThemeClass}`}>
        <LedgerSummaryStrip
          totalRecords={totalRecords}
          totalOutstanding={formatCurrency(totalOutstanding)}
          searchTerm={searchTerm}
          onSearchChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(0);
          }}
          showZeroOutstanding={showZeroOutstanding}
          onToggleZeroOutstanding={() => {
            setShowZeroOutstanding(!showZeroOutstanding);
            setCurrentPage(0);
          }}
        />

        <div className='ledger-table-container shadow-inner rounded-lg bg-white p-1.5'>
          <LedgerTable
            data={ledgerRecords}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            totalRecords={totalRecords}
            formatCurrency={formatCurrency}
            onRowClick={handleRowClick}
            onPageChange={(p) => setCurrentPage(p)}
            onRowsPerPageChange={(v) => {
              setRowsPerPage(v);
              setCurrentPage(0);
            }}
            onSortChange={handleSortChange}
            sortField={sortField}
            sortOrder={sortOrder}
          />
        </div>
      </div>

      {loading && <LoadingScreen />}
    </div>
  );
};

export default Ledger;